import { useForm } from '@tanstack/react-form';

import { FormDialog } from '@/components/composed/FormDialog';
import { FormField } from '@/components/composed/FormField';
import { fieldErrorMessages } from '@/lib/forms/field-errors';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { notify } from '@/components/composed/notify';
import { ApiError } from '@/lib/api/errors';
import {
  agentBrokerFormSchema,
  toAgentBrokerInput,
  type AgentBrokerFormValues,
} from '../schemas/agent-broker-form';
import { useCreateAgentBroker } from '../api/agent-broker-mutations';

export function AgentBrokerFormDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const createAgentBroker = useCreateAgentBroker();

  const form = useForm({
    defaultValues: {
      name: '',
      dbaName: '',
      agentType: '',
      npn: '',
      status: 'active',
    } as AgentBrokerFormValues,
    validators: {
      onChange: agentBrokerFormSchema,
      onSubmitAsync: async ({ value }) => {
        try {
          await createAgentBroker.mutateAsync(toAgentBrokerInput(value));
          return null;
        } catch (error) {
          if (error instanceof ApiError && error.fieldErrors?.length) {
            const fields: Record<string, string> = {};
            for (const fe of error.fieldErrors) fields[fe.field] = fe.message;
            return { fields };
          }
          return {
            form:
              error instanceof ApiError
                ? error.message
                : 'Failed to create agent/broker.',
          };
        }
      },
    },
    onSubmit: () => {
      notify.success('Agent/broker created.');
      onOpenChange(false);
    },
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Create agent / broker"
      onSubmit={() => void form.handleSubmit()}
      submitLabel="Create"
      pending={createAgentBroker.isPending}
    >
      <form.Field name="name">
        {(field) => (
          <FormField
            label="Name"
            errors={fieldErrorMessages(field.state.meta.errors)}
          >
            {({ id }) => (
              <Input
                id={id}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
              />
            )}
          </FormField>
        )}
      </form.Field>

      <form.Field name="dbaName">
        {(field) => (
          <FormField label="DBA name">
            {({ id }) => (
              <Input
                id={id}
                value={field.state.value ?? ''}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
              />
            )}
          </FormField>
        )}
      </form.Field>

      <form.Field name="agentType">
        {(field) => (
          <FormField label="Type">
            {({ id }) => (
              <Input
                id={id}
                value={field.state.value ?? ''}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
              />
            )}
          </FormField>
        )}
      </form.Field>

      <form.Field name="npn">
        {(field) => (
          <FormField label="NPN">
            {({ id }) => (
              <Input
                id={id}
                value={field.state.value ?? ''}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
              />
            )}
          </FormField>
        )}
      </form.Field>

      <form.Field name="status">
        {(field) => (
          <FormField label="Status">
            {({ id }) => (
              <Select
                value={field.state.value}
                onValueChange={(v) =>
                  field.handleChange(v as AgentBrokerFormValues['status'])
                }
              >
                <SelectTrigger id={id}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            )}
          </FormField>
        )}
      </form.Field>
    </FormDialog>
  );
}
